import React, { useEffect, useState } from 'react';

/**
 * Cinematic loading intro shown once per session.
 *
 * Sequence (~2.0s total):
 *   1. The STIG ROCK logo fades in centred on a deep dark backdrop.
 *   2. A luminous horizontal scan-line sweeps from left edge through the logo
 *      and out the right edge.
 *   3. The whole intro mask wipes upward (transform translateY) to reveal the page.
 *
 * After the first run within a tab session it never re-shows — controlled via
 * sessionStorage so navigation back/forward feels natural.
 */
export default function CinematicIntro() {
  const [phase, setPhase] = useState('init'); // init → revealed → swept → done → hidden

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.sessionStorage.getItem('stig-intro-played') === '1') {
      setPhase('hidden');
      return;
    }
    document.documentElement.style.overflow = 'hidden';
    const t1 = setTimeout(() => setPhase('revealed'), 80);
    const t2 = setTimeout(() => setPhase('swept'), 900);
    const t3 = setTimeout(() => setPhase('done'), 1700);
    const t4 = setTimeout(() => {
      setPhase('hidden');
      document.documentElement.style.overflow = '';
      window.sessionStorage.setItem('stig-intro-played', '1');
    }, 2300);
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, []);

  if (phase === 'hidden') return null;

  return (
    <div
      data-testid="cinematic-intro"
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: '#050608',
        transform: phase === 'done' ? 'translateY(-100%)' : 'translateY(0)',
        transition: 'transform 700ms cubic-bezier(0.7, 0, 0.3, 1)',
        overflow: 'hidden',
      }}
    >
      {/* Logo */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          opacity: phase === 'init' ? 0 : 1,
          transition: 'opacity 700ms ease',
        }}
      >
        <img
          src="/brand/stig-rock-logo-white.png"
          alt=""
          style={{
            height: 'clamp(70px, 9vw, 130px)',
            width: 'auto',
            filter:
              phase === 'revealed' || phase === 'swept'
                ? 'drop-shadow(0 0 24px rgba(248,197,96,0.35))'
                : 'none',
            transition: 'filter 600ms ease',
          }}
          draggable={false}
        />
      </div>

      {/* Luminous scan line */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: '180px',
          background:
            'linear-gradient(90deg, transparent 0%, rgba(255,235,180,0.0) 20%, rgba(255,235,180,0.85) 50%, rgba(255,235,180,0.0) 80%, transparent 100%)',
          filter: 'blur(2px)',
          mixBlendMode: 'screen',
          transform:
            phase === 'swept' || phase === 'done'
              ? 'translateX(100vw)'
              : 'translateX(-200px)',
          transition: 'transform 1100ms cubic-bezier(0.55, 0.1, 0.3, 1)',
        }}
      />

      {/* Soft vignette */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at center, rgba(0,0,0,0) 30%, rgba(0,0,0,0.8) 100%)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
